import type { IntlShape, MessageDescriptor } from 'react-intl-next';

import {
	cadenceKeywordMessages,
	parentKeywordMessages,
	recurrenceKeywordMessages,
	titleKeywordMessages,
	statusUpdateKeywordMessages,
} from './statusUpdateSignalKeywordMessages.ts';

const materialize = (intl: IntlShape, messages: Record<string, MessageDescriptor>): string[] =>
	Object.values(messages).reduce<string[]>((keywords, descriptor) => {
		const keyword = intl.formatMessage(descriptor);
		if (keyword.trim().length > 0) {
			keywords.push(keyword);
		}
		return keywords;
	}, []);

/**
 * Resolves the (localized) title keyword list for the current locale. Seeded in
 * English; falls back to `defaultMessage` when a locale has no translation.
 */
export const getTitleKeywords = (intl: IntlShape): string[] =>
	materialize(intl, titleKeywordMessages);

/** Resolves the (localized) parent page/folder keyword list for the current locale. */
export const getParentKeywords = (intl: IntlShape): string[] =>
	materialize(intl, parentKeywordMessages);

/**
 * Localized long and short month names for the current locale, each paired with
 * its zero-based month index.
 */
const getMonthEntries = (intl: IntlShape): { name: string; index: number }[] => {
	const entries: { name: string; index: number }[] = [];
	for (let month = 0; month < 12; month++) {
		const date = new Date(Date.UTC(2021, month, 1));
		entries.push({ name: intl.formatDate(date, { month: 'long', timeZone: 'UTC' }), index: month });
		entries.push({
			name: intl.formatDate(date, { month: 'short', timeZone: 'UTC' }),
			index: month,
		});
	}
	return entries;
};

/** Localized long and short month names for the current locale (e.g. "August", "Aug"). */
export const getMonthNames = (intl: IntlShape): string[] =>
	getMonthEntries(intl).map((entry) => entry.name);

/**
 * Maps each localized month name to its zero-based month index for the current locale.
 */
export const getMonthIndexByName = (intl: IntlShape): Map<string, number> =>
	new Map(getMonthEntries(intl).map((entry) => [entry.name.toLowerCase(), entry.index]));

/**
 * Resolves the (localized) recurrence indicators for the current locale: the
 * cadence adjectives, the extra recurrence words ("week", "end of week") and
 * localized month names. Paired (AND) with `getUpdateWordKeywords` to detect a
 * recurring status-update title.
 */
export const getRecurrenceKeywords = (intl: IntlShape): string[] => [
	...materialize(intl, cadenceKeywordMessages),
	...materialize(intl, recurrenceKeywordMessages),
	...getMonthNames(intl),
];

/** Resolves the (localized) update words ("update", "status", "recap", "report"). */
export const getStatusUpdateKeywords = (intl: IntlShape): string[] =>
	materialize(intl, statusUpdateKeywordMessages);
